<template>
    <nav-slot
        v-if="$slots.header && fixedHeader && !isInPageMode"
        ref="headerNav"
        uniqueKey="header"
        :horizontal="isHorizontal"
        :extraProps="extraProps"
        :navClass="headerClass"
        :navStyle="headerStyle"
        :wrapperRect="wrapperRect"
        :contentRect="contentRect"
        :innerFixed="true"
        :fixedWrapperClass="fixedHeaderWrapperClass"
        :fixedWrapperStyle="fixedHeaderWrapperStyle"
        @resize="onSlotResize"
    >
        <slot name="header"></slot>
    </nav-slot>
    <pt-scrollbar
        :class="class"
        :style="style"
        ref="ptScrollRef"
        throttleType="throttle"
        :throttleWait="isHorizontal ? 80 : 500"
        :contentClass="$style.contentClass"
        :direction="direction"
        :autoHide="autoHide"
        :fixedThumb="fixedThumb"
        :nativeScroll="nativeScroll"
        @wrapper-resize="onWrapperResize"
        @content-resize="onContentResize"
        @scroll="onHandleSyncFixedNav"
        @scrollInfo="onHandleScrollInfoChange"
    >
        <div
            v-if="props.scrollRightButton && scrollToRight"
            :class="$style['content-header_right_scroll']"
            @click="scrollRight"
        >
            <pt-icon
                icon="pt-icon-asc"
                :style="{ width: '14px', height: '14px', fill: '#8993A4', transform: 'rotate(90deg)' }"
            />
        </div>
        <nav-slot
            v-if="$slots.header && (isInPageMode || !(fixedHeader === true || typeof fixedHeader === 'number'))"
            ref="headerNav"
            uniqueKey="header"
            :horizontal="isHorizontal"
            :extraProps="extraProps"
            :navClass="headerClass"
            :navStyle="headerStyle"
            :outerFixedPosition="fixedHeader"
            :wrapperRect="wrapperRect"
            :fixedWrapperClass="fixedHeaderWrapperClass"
            :fixedWrapperStyle="fixedHeaderWrapperStyle"
            @resize="onSlotResize"
        >
            <slot name="header"></slot>
        </nav-slot>
        <div
            :class="[contentClass, $style.itemWrapperClass, { [$style.horizontal]: isHorizontal }]"
            :style="{ ...contentStyle, ...paddingStyle }"
        >
            <div v-if="loading" v-loading="loading" :class="$style.loading"></div>
            <item
                v-for="item in renderRange"
                :class="[itemClass, $style['virtualizd-item'], item.source?._class]"
                :style="itemStyle"
                :index="item.index"
                :source="item.source"
                :uniqueKey="item.uniqueKey"
                :horizontal="isHorizontal"
                :component="dataComponent"
                :extraProps="extraProps"
                :isSizeImmutable="isSizeImmutable"
                @resize="onItemResize"
                @click="onClickRow(item.uniqueKey, item.source)"
            />
        </div>
        <nav-slot
            v-if="$slots.footer && (isInPageMode || !(fixedFooter === true || typeof fixedFooter === 'number'))"
            ref="footerNav"
            uniqueKey="footer"
            :horizontal="isHorizontal"
            :extraProps="extraProps"
            :navClass="footerClass"
            :navStyle="footerStyle"
            :outerFixedPosition="fixedFooter"
            :wrapperRect="wrapperRect"
            :fixedWrapperClass="fixedFooterWrapperClass"
            :fixedWrapperStyle="fixedFooterWrapperStyle"
            @resize="onSlotResize"
        >
            <slot name="footer"></slot>
        </nav-slot>
        <div ref="shepherd" :class="[$style.shepherd, $style[direction]]" />
    </pt-scrollbar>
    <nav-slot
        v-if="$slots.footer && fixedFooter && !isInPageMode"
        ref="footerNav"
        uniqueKey="footer"
        :horizontal="isHorizontal"
        :extraProps="extraProps"
        :navClass="footerClass"
        :navStyle="footerStyle"
        :wrapperRect="wrapperRect"
        :contentRect="contentRect"
        :innerFixed="true"
        :fixedWrapperClass="fixedHeaderWrapperClass"
        :fixedWrapperStyle="fixedHeaderWrapperStyle"
        @resize="onSlotResize"
    >
        <slot name="footer"></slot>
    </nav-slot>
</template>

<script lang="ts">
export default {
    name: 'PtVirtualized',
};
</script>

<script lang="ts" setup>
import { ref, reactive, computed, watch, onActivated, onMounted, onUnmounted, provide } from 'vue';
import type { ComponentPublicInstance, CSSProperties } from 'vue';
import PtScrollbar from '../../scrollbar';
import Virtual from './virtualCore';
import isDOMElement from '../../../utils/isDOMElement';
import getDocumentScroll from '../../../utils/getDocumentScroll';
import NavSlot from './nav-slot.vue';
import Item from './itemV.vue';
import debounce from 'lodash/debounce';

interface Props {
    dataKey: string;
    dataSources: Array<any>;
    dataComponent: ComponentPublicInstance;
    extraProps?: Record<string, any>;
    keeps?: number;
    estimateSize?: number;
    isSizeImmutable?: boolean;
    direction?: 'vertical' | 'horizontal';
    start?: number;
    offset?: number;
    /** 'documentScroll'(原生页面滚动条) | string(querySelector) | HTMLElement */
    scrollContainerEle?: any; // 这里不能写HTMLElement，devtools会报错
    class?: string;
    style?: CSSProperties;
    contentClass?: string;
    contentStyle?: CSSProperties;
    itemClass?: string;
    itemStyle?: CSSProperties;
    headerClass?: string;
    headerStyle?: CSSProperties;
    footerClass?: string;
    footerStyle?: CSSProperties;
    fixedThumb?: boolean;
    fixedHeader?: number | boolean;
    fixedFooter?: number | boolean;
    fixedHeaderWrapperClass?: string;
    fixedHeaderWrapperStyle?: CSSProperties;
    fixedFooterWrapperClass?: string;
    fixedFooterWrapperStyle?: CSSProperties;
    autoHide?: boolean;
    nativeScroll?: boolean;
    scrollRightButton?: boolean;
    loading?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    estimateSize: 80,
    direction: 'vertical',
    start: 0,
    offset: 0,
    autoHide: true,
    fixedThumb: false,
    isSizeImmutable: false,
    nativeScroll: true,
    scrollRightButton: false,
    loading: false,
});
const emit = defineEmits(['scroll', 'scrollInfo', 'wrapperResize', 'contentResize', 'clickRow']);

const keepsInner = computed(
    () =>
        props.keeps ??
        Math.ceil(window.screen[props.direction === 'vertical' ? 'height' : 'width'] / (props.estimateSize ?? 80) + 7),
);

/** 包裹容器大小 */
const wrapperRect = reactive({
    width: 0,
    height: 0,
    top: 0,
    left: 0,
});
const contentRect = reactive({
    width: 0,
    height: 0,
    top: 0,
    left: 0,
});

/** 确定滚动容器 */
const isInPageMode = ref(false);
let scrollContainerEle: HTMLElement;
const ptScrollRef = ref<typeof PtScrollbar>();
provide('scrollWrapperRef', ptScrollRef);

onMounted(() => {
    /** 内部滚动容器 */
    if (ptScrollRef.value) scrollContainerEle = ptScrollRef.value.scrollEl;

    /** 如果指定了外部滚动容器，优先级更高 */
    if (props.scrollContainerEle) {
        let outerScrollContainerEle: HTMLElement | null = null;
        if (typeof props.scrollContainerEle === 'string') {
            if (props.scrollContainerEle === 'documentScroll')
                outerScrollContainerEle = getDocumentScroll() as HTMLElement;
            else outerScrollContainerEle = document.querySelector(props.scrollContainerEle) as HTMLElement;
        } else if (isDOMElement(props.scrollContainerEle)) outerScrollContainerEle = props.scrollContainerEle;
        if (!outerScrollContainerEle) return;
        /** 指定的外部滚动容器有效时候，才会去替换内部滚动容器 */
        scrollContainerEle = outerScrollContainerEle;
        isInPageMode.value = true;
    }
});

/** 底部定位元素 */
const shepherd = ref<HTMLDivElement>(null!);

const range = reactive({
    start: 0,
    end: 0,
    padFront: 0,
    padBehind: 0,
});
let virtual: Virtual;

const isHorizontal = props.direction === 'horizontal';
const directionKey = props.direction === 'horizontal' ? 'scrollLeft' : 'scrollTop';
const clientSizeKey = props.direction === 'horizontal' ? 'clientWidth' : 'clientHeight';
const scrollSizeKey = props.direction === 'horizontal' ? 'scrollWidth' : 'scrollHeight';
const offsetDirectionKey = props.direction === 'horizontal' ? 'offsetLeft' : 'offsetTop';
const scrollToRight = ref(false);
// ----------- public method start -----------

// return current scroll offset
const getOffset = () => Math.ceil(scrollContainerEle[directionKey] ?? 0);

// return client viewport size
const getClientSize = () => Math.ceil(scrollContainerEle[clientSizeKey] ?? 0);

// return all scroll size
const getScrollSize = () => Math.ceil(scrollContainerEle[scrollSizeKey] ?? 0);

// set current scroll position to a expectant offset
const scrollToOffset = (offset: number) => (scrollContainerEle[directionKey] = offset);

// set current scroll position to a expectant index
const scrollToIndex = (index: number) => {
    // scroll to bottom
    if (index >= props.dataSources.length - 1) scrollToBottom();
    else scrollToOffset(virtual.getOffset(index));
};

// 点击滚动到右侧
const scrollRight = () => {
    const _scrollEl = ptScrollRef.value?.scrollEl;
    if (!_scrollEl) return;
    const _scrollWidth = _scrollEl.scrollWidth;
    const _scrollLeft = _scrollEl.scrollLeft;
    const _clientWidth = _scrollEl.clientWidth;
    // 滚动条左右偏离值
    const _offsetWidth = 2;
    // 点击时每次滚动一个列表的宽度，直到最右侧
    if (_scrollWidth > _scrollLeft + _clientWidth + _offsetWidth) {
        _scrollEl.scrollLeft = _scrollLeft + _clientWidth;
    }
};

// set current scroll position to bottom
const scrollToBottom = () => {
    if (!shepherd.value) return;
    const offset = shepherd.value[offsetDirectionKey];
    scrollToOffset(offset);

    // check if it's really scrolled to the bottom
    // maybe list doesn't render and calculate to last range
    // so we need retry in next event loop until it really at bottom
    setTimeout(() => {
        if (getOffset() + getClientSize() < getScrollSize()) scrollToBottom();
    }, 4);
};

// when using page mode we need update slot header size manually
// taking root offset relative to the browser as slot header size
const updatePageModeInfo = () => {
    if (!isInPageMode.value) return;
    const scrollTarget = !isDOMElement(scrollContainerEle) ? window : scrollContainerEle;
    const offsetFront = isHorizontal
        ? wrapperRect.left +
          (scrollTarget === window ? scrollTarget?.pageXOffset : (scrollTarget as HTMLElement).scrollLeft)
        : wrapperRect.top +
          (scrollTarget === window ? scrollTarget?.pageYOffset : (scrollTarget as HTMLElement).scrollTop);
    virtual.updateParam('slotHeaderSize', offsetFront);
};

// reset all state back to initial
const reset = () => {
    virtual.destroy();
    scrollToOffset(0);
    installVirtual();
};

// ----------- public method end -----------

const getUniqueIdFromDataSources = () => {
    return props.dataSources.map((dataSource) => dataSource?.[props.dataKey]);
};

const installVirtual = () => {
    virtual = new Virtual(
        {
            slotHeaderSize: 0,
            slotFooterSize: 0,
            keeps: keepsInner.value,
            estimateSize: props.estimateSize,
            buffer: Math.round(keepsInner.value / 3), // recommend for a third of keeps
            uniqueIds: getUniqueIdFromDataSources(),
        },
        onRangeChanged,
    );

    // sync initial range
    Object.assign(range, virtual.getRange());
};

// event called when each item mounted or size changed
const onItemResize = (id: string | number, size: number) => virtual.saveSize(id, size);

// event called when slot mounted or size changed
const onSlotResize = (type: 'header' | 'footer', size: number, hasInit: boolean) => {
    if (type === 'header' && !isInPageMode.value) virtual.updateParam('slotHeaderSize', size);
    else if (type === 'footer') virtual.updateParam('slotFooterSize', size);
    if (hasInit) virtual.handleSlotSizeChange();
};

// here is the rerendering entry
const onRangeChanged = (newRange: any) => Object.assign(range, newRange);

const onScroll = (evt?: Event) => {
    const offset = getOffset();
    const clientSize = getClientSize();
    const scrollSize = getScrollSize();

    // iOS scroll-spring-back behavior will make direction mistake
    if (offset < 0 || offset + clientSize > scrollSize + 1 || !scrollSize) return;

    virtual.handleScroll(offset);
    emit('scroll', evt);
};

const headerNav = ref<typeof NavSlot>(null!);
const footerNav = ref<typeof NavSlot>(null!);

// 浮动首尾情况下滚动辅轴滚动条
const onHandleSyncFixedNav = () => {
    if (headerNav.value) headerNav.value.syncScroll();
    if (footerNav.value) footerNav.value.syncScroll();
};

const onHandleScrollInfoChange = (evt: any) => {
    onShowScrollRightButton();
    emit('scrollInfo', evt);
};

const onShowScrollRightButton = debounce(() => {
    const _scrollEl = ptScrollRef.value?.scrollEl;
    // 计算滚动长度与滚动距离和元素宽度的偏离值
    const _offsetWidth = 2;
    scrollToRight.value = _scrollEl
        ? _scrollEl.scrollWidth > _scrollEl.scrollLeft + _scrollEl.clientWidth + _offsetWidth
        : false;
}, 300);

const onWrapperResize = (rect: { width: number; height: number; left: number; top: number }) => {
    emit('wrapperResize', rect);
    Object.assign(wrapperRect, rect);
};
const onContentResize = (rect: { width: number; height: number; left: number; top: number }) => {
    emit('contentResize', rect);
    if ((props.fixedHeader || props.fixedFooter) && !isInPageMode.value) {
        Object.assign(contentRect, rect);
    }
};

const onClickRow = (uniqueKey: string, source: any) => {
    emit('clickRow', { dataKey: uniqueKey, source });
};

const renderRange = computed(() => {
    const render = [];
    for (let index = range.start; index <= range.end; index++) {
        const dataSource = props.dataSources[index];
        if (!dataSource) console.warn(`Cannot get the index '${index}' from data-sources.`);
        const uniqueKey = dataSource?.[props.dataKey];
        if (uniqueKey === null || uniqueKey === undefined)
            console.warn(`Cannot get the data-key '${props.dataKey}' from data-sources.`);
        render.push({
            index,
            uniqueKey,
            source: dataSource,
        });
    }
    return render;
});

const paddingStyle = computed(() => ({
    padding: isHorizontal
        ? `0px ${range.padBehind}px 0px ${range.padFront}px`
        : `${range.padFront}px 0px ${range.padBehind}px`,
}));

watch(
    () => props.dataSources,
    () => {
        virtual.updateParam('uniqueIds', getUniqueIdFromDataSources());
        virtual.handleDataSourcesChange();
    },
);

watch(
    () => keepsInner.value,
    (newValue) => {
        virtual.updateParam('keeps', newValue);
        virtual.handleSlotSizeChange();
    },
);

watch(
    () => props.start,
    (newValue) => {
        scrollToIndex(newValue);
    },
);

watch(
    () => props.offset,
    (newValue) => {
        scrollToOffset(newValue);
    },
);

watch(wrapperRect, updatePageModeInfo);

onActivated(() => scrollToOffset(virtual.offset));

onMounted(() => {
    if (props.start) scrollToIndex(props.start);
    else if (props.offset) scrollToOffset(props.offset);

    if (isInPageMode.value) updatePageModeInfo();

    const listenerTarget = !isDOMElement(scrollContainerEle) ? window : scrollContainerEle;
    listenerTarget.addEventListener('scroll', onScroll, { passive: false });
    onShowScrollRightButton();
});

onUnmounted(() => {
    virtual?.destroy?.();
    const listenerTarget = !isDOMElement(scrollContainerEle) ? window : scrollContainerEle;
    listenerTarget.removeEventListener('scroll', onScroll);
});

installVirtual();

const scrollEl = computed(() => ptScrollRef.value?.scrollEl);
defineExpose({
    reset,
    scrollEl,
});
</script>

<style lang="scss" module>
.contentClass {
    min-width: 100%;
    width: fit-content;
}
.content-header_right_scroll {
    position: absolute;
    z-index: 100;
    right: 0;
    width: 30px;
    background-color: #fff;
    box-shadow: -3px 0 6px 0 RGBA(0, 0, 0, 0.04);
    height: 56px;
    text-align: center;
    line-height: 56px;
    cursor: pointer;
}
.itemWrapperClass {
    min-width: 100%;
    width: fit-content;
    min-height: 58px;
    contain: layout style paint;
    position: relative;
    &.horizontal {
        display: inline-flex;
    }
}
.loading {
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    min-height: 58px;
    width: 100%;
}
.virtualizd-item {
    contain: layout style paint;
}

.shepherd {
    &.vertical {
        width: 100%;
        height: 0;
    }
    &.horizontal {
        width: 0;
        height: 100%;
    }
    contain: strict;
    content-visibility: auto;
}
</style>
